import { useEffect, useState } from "react";
import { proxy, useSnapshot } from "valtio";
import Api from "../state/Api";
import { configStore, loginStore } from "../state/CommonState";
import aicharges from "../assets/images/aicharges.svg";

// Row keys that are not rate values (kept from the existing row when a parsed charge fills it)
const NON_VALUE_KEYS = ['id', 'isIncluded', 'chargeDescription', 'category', 'isNew', 'disabled'];

const descKey = (d) => String(d ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Merges parsed charges into a charges list (returns a new list).
 * A parsed charge fills the row with the same description, else the first blank row, else it is added at the end.
 * newRec: defaults for an added row (e.g. { category: 'FREIGHT', isNew: 'Y' }).
 */
export function mergeParsedCharges(list, parsed, newRec = {}) {
    const rows = JSON.parse(JSON.stringify(list || []));
    let added = 0, updated = 0;
    (parsed || []).forEach((p) => {
        if (!p || !String(p.chargeDescription ?? '').trim()) return;
        const values = {};
        Object.keys(p).forEach((k) => { if (!NON_VALUE_KEYS.includes(k)) values[k] = p[k] });
        let row = rows.find((r) => r.chargeDescription !== 'Volume Discount' && descKey(r.chargeDescription) === descKey(p.chargeDescription));
        if (row) {
            // Keep a disabled cell disabled
            (row.disabled || []).forEach((k) => delete values[k]);
            Object.assign(row, values);
            updated++;
            return;
        }
        // Category from the AI response for a blank or added row; a row matched by description keeps its own
        const category = p?.category ? { category: p.category } : {};
        row = rows.find((r) => !String(r.chargeDescription ?? '').trim());
        if (row) {
            Object.assign(row, values, category, { chargeDescription: p.chargeDescription });
            updated++;
            return;
        }
        rows.push({ ...JSON.parse(JSON.stringify(newRec)), ...values, ...category, chargeDescription: p.chargeDescription, id: rows.length + 1 });
        added++;
    });
    return { list: rows, added, updated };
}

/**
 * Currencies of the parsed charges against the currencies in our system (configStore.currencyList).
 * A charge in a currency we do not have is not added; it is reported as an error instead.
 * Returns { chargesList (only charges we can add), unavailable: { RMB: ['HDL', 'D/O', ...] } }
 */
async function checkCurrencies(parsed) {
    if (!configStore.currencyList?.length) await Api.getExchangerates();
    const available = new Map(
        (configStore.currencyList || [])
            .map((e) => String(e?.currency ?? e ?? '').trim())
            .filter(Boolean)
            .map((c) => [c.toUpperCase(), c])
    );
    const unavailable = {};
    if (!available.size) return { chargesList: parsed, unavailable }; // currency list not loaded: nothing to check against
    const chargesList = [];
    parsed.forEach((c) => {
        const cur = String(c?.currency ?? '').trim();
        if (!cur) { chargesList.push(c); return }
        const known = available.get(cur.toUpperCase());
        if (known) { chargesList.push({ ...c, currency: known }); return }
        (unavailable[cur.toUpperCase()] = unavailable[cur.toUpperCase()] || []).push(c.chargeDescription);
    });
    return { chargesList, unavailable };
}

const unavailableCurrencyNotes = (unavailable) => Object.entries(unavailable).map(([cur, descs]) =>
    `Currency ${cur} is not available in our system, so these charges were not added: ${descs.join(', ')}.`);

/**
 * Icon for a charges heading: opens a text box, the pasted rate text is parsed on the server
 * (POST /api/v1/ai/parse-charges) and onCharges(chargesList) fills the table.
 * shipmentType: 'LCL' | 'Air' | 'FCL'; containerTypes: FCL container types, e.g. ["20'STD", "40'HC"].
 */
export default function AiChargeParse({ shipmentType, containerTypes = [], onCharges, disabled, historyScope, historyKey = 'list' }) {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    if (disabled) return null;

    const close = () => { if (!loading) { setOpen(false); setError('') } };
    const parse = () => {
        if (!text.trim()) { setError('Please paste the rates text.'); return }
        setLoading(true);
        setError('');
        const data = { shipmentType, dataSet: text };
        if (shipmentType === 'FCL') data.containerTypes = containerTypes;
        Api.parseCharges(data, async (res) => {
            if (!res?.success) { setLoading(false); setError(res?.message || 'Could not read the charges.'); return }
            if (!res.chargesList?.length) { setLoading(false); setError('No charges found in this text.'); return }
            const { chargesList, unavailable } = await checkCurrencies(res.chargesList);
            setLoading(false);
            // Every charge is in a currency we do not have: nothing to add, show the error in the dialog
            if (!chargesList.length) { setError(unavailableCurrencyNotes(unavailable).join(' ')); return }
            // What was pasted, by whom and when (the screen may keep it, e.g. the quotation's AI charge history)
            const entry = {
                text,
                pastedBy: loginStore.email || '',
                pastedOn: Date.now(),
                shipmentType,
                chargesFilled: chargesList.length,
                notAdded: unavailable,
            };
            if (historyScope) addAiHistory(historyScope, entry, historyKey);
            const msg = onCharges(chargesList, entry);
            setOpen(false);
            setText('');
            const warnings = unavailableCurrencyNotes(unavailable);
            const notes = [msg, ...warnings].filter(Boolean);
            // A missing-currency warning stays open until closed (the plain summary still auto-closes)
            if (notes.length) configStore.setModalMessage(notes.join(' '), [], warnings.length === 0);
        }, () => setLoading(false));
    };

    return (
        <>
            <img src={aicharges} alt="Fill charges from text" title="Paste rates text to fill the charges"
                style={{ height: '28px', verticalAlign: 'middle', marginLeft: '10px', cursor: 'pointer' }}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true) }} />
            {open && <div className="modal is-active" style={{ zIndex: 1000 }} onClick={(e) => e.stopPropagation()}>
                <div className="modal-background" onClick={close}></div>
                <div className="modal-card" style={{ width: '600px', maxWidth: '95vw', borderRadius: '10px' }}>
                    <section className="modal-card-body">
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                            <img src={aicharges} alt="" style={{ height: '28px', marginRight: '8px' }} />
                            <b style={{ fontSize: '15px' }}>Fill {shipmentType} charges from text</b>
                        </div>
                        <textarea className="textarea" rows={8} autoFocus value={text} disabled={loading}
                            placeholder={shipmentType === 'FCL' ? "e.g. Ocean Freight USD 1200/20', USD 2100/40HC. THC INR 9500 per 20'. BL fee INR 4500 per BL"
                                : shipmentType === 'Air' ? 'e.g. Air Freight USD 3.2/kg min USD 75. AWB fee INR 1500'
                                    : 'e.g. Ocean Freight: USD 28/CBM, Min USD 55. THC: INR 3850/BL'}
                            onChange={(e) => setText(e.target.value)} />
                        {error && <p className="help is-danger" style={{ fontSize: '13px' }}>{error}</p>}
                        <p className="help">Charges with the same description are updated, the others are added. Please check the rates before saving.</p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
                            <button className="button is-small" disabled={loading} onClick={close}>Cancel</button>
                            <button className={`button is-small ${loading ? 'is-loading' : ''}`} disabled={loading}
                                style={{ backgroundColor: process.env.REACT_APP_ENTERPRISE_COLOUR_BUTTON, color: '#fff' }}
                                onClick={parse}>Fill Charges</button>
                        </div>
                    </section>
                </div>
            </div>}
        </>
    );
}

/**
 * AI history kept beside a record until it is saved (the charge screens rebuild their forms whenever their
 * record changes, so the history must not be written into the record while editing).
 *   scope:     one per screen, e.g. 'updateCost', 'rateRequest'
 *   recordKey: the record being edited; a different record reloads the scope from its saved history
 * Save: put getAiHistory(scope) into the saved document as aiChargeHistory.
 */
export const aiHistoryStore = proxy({ lists: {}, keys: {} });

export function loadAiHistory(scope, recordKey, initial) {
    const key = String(recordKey ?? '');
    if (aiHistoryStore.keys[scope] === key && Array.isArray(aiHistoryStore.lists[scope])) return;
    aiHistoryStore.keys[scope] = key;
    aiHistoryStore.lists[scope] = Array.isArray(initial) ? JSON.parse(JSON.stringify(initial)) : [];
}

export function addAiHistory(scope, entry, chargeKey = 'list') {
    if (!scope || !entry) return;
    const cur = Array.isArray(aiHistoryStore.lists[scope]) ? aiHistoryStore.lists[scope] : [];
    aiHistoryStore.lists[scope] = [...cur, { ...entry, chargeKey }];
}

export const getAiHistory = (scope) =>
    JSON.parse(JSON.stringify(Array.isArray(aiHistoryStore.lists[scope]) ? aiHistoryStore.lists[scope] : []));

// Keeps an AI fill on a record (e.g. quoteDataRec, saved with the quotation): rec.aiChargeHistory[]
export function addAiChargeHistory(rec, entry, chargeKey = 'list') {
    if (!rec || !entry) return;
    rec.aiChargeHistory = [...(Array.isArray(rec.aiChargeHistory) ? rec.aiChargeHistory : []), { ...entry, chargeKey }];
}

const fmtWhen = (ms) => {
    const d = new Date(Number(ms));
    return isNaN(d.getTime()) ? '' : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/**
 * History of AI fills for one charge table, shown above it: who pasted what, and when (latest first).
 * history: rec.aiChargeHistory; chargeKey: the table's key ('list' for LCL, the charge key for FCL / Air),
 * or 'all' for every table (booking views).
 */
export function AiChargeHistory({ history, chargeKey = 'list', scope, recordKey, initial }) {
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState(-1);
    const snap = useSnapshot(aiHistoryStore);
    // With a scope: show the scope's list (saved + not yet saved). Screens usually load it themselves
    // (loadAiHistory); with a recordKey this component loads it once per record.
    useEffect(() => { if (scope && recordKey !== undefined) loadAiHistory(scope, recordKey, initial) }, [scope, recordKey]);
    const list = scope ? snap.lists[scope] : history;
    const rows = (Array.isArray(list) ? list : [])
        .filter((h) => chargeKey === 'all' || (h.chargeKey || 'list') === chargeKey)
        .slice()
        .reverse();
    if (!rows.length) return null;
    return (
        <div style={{ margin: '8px 0 10px', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#fafbfc', fontSize: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', cursor: 'pointer' }}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(!open) }}>
                <img src={aicharges} alt="" style={{ height: '18px' }} />
                <b>AI charge history ({rows.length})</b>
                <span style={{ color: '#6b7280' }}>Last: {rows[0].pastedBy || '—'}, {fmtWhen(rows[0].pastedOn)}</span>
                <span style={{ marginLeft: 'auto', color: '#6b7280' }}>{open ? 'Hide ▲' : 'Show ▼'}</span>
            </div>
            {open && (
                <div style={{ borderTop: '1px solid #e5e7eb' }}>
                    {rows.map((h, i) => {
                        const notAdded = Object.entries(h.notAdded || {}).map(([cur, descs]) => `${cur}: ${(descs || []).join(', ')}`).join('; ');
                        return (
                            <div key={i} style={{ padding: '6px 10px', borderBottom: i < rows.length - 1 ? '1px solid #eef0f3' : 'none' }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                                    <span><b>{fmtWhen(h.pastedOn)}</b></span>
                                    <span>by <b>{h.pastedBy || '—'}</b></span>
                                    <span>{h.chargesFilled ?? 0} charge{h.chargesFilled == 1 ? '' : 's'} filled</span>
                                    {notAdded && <span style={{ color: '#b91c1c' }}>Not added (currency not in system): {notAdded}</span>}
                                    <a href="#" style={{ marginLeft: 'auto' }}
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded(expanded === i ? -1 : i) }}>
                                        {expanded === i ? 'Hide text' : 'Show pasted text'}
                                    </a>
                                </div>
                                {expanded === i && (
                                    <pre style={{ margin: '6px 0 0', padding: '8px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', whiteSpace: 'pre-wrap', fontSize: '12px', maxHeight: '220px', overflow: 'auto' }}>{h.text}</pre>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export const parsedSummary = ({ added, updated }) => `${updated} charge${updated == 1 ? '' : 's'} updated, ${added} added. Please check the rates.`;

// Valtio charges store (store[key] list): merge, then recalc() the totals. Returns the summary message.
export function fillChargesStore(store, key, parsed, recalc) {
    const r = mergeParsedCharges(store[key], parsed, { ...JSON.parse(JSON.stringify(store.newRec || {})), onaccount: 'On Your Account' });
    store[key] = r.list;
    if (recalc) recalc();
    return parsedSummary(r);
}

// react-hook-form LCL charges (useFieldArray `listkey`): totals are recalculated by the form on change.
export function fillChargesForm(getValues, setValue, listkey, parsed) {
    const r = mergeParsedCharges(getValues(listkey), parsed, { currency: 'INR', onaccount: 'On Your Account', category: 'FREIGHT', perCBM: 0, perTon: 0, minimum: 0, maximum: 0, perShipment: 0, total: 0, isNew: 'Y' });
    setValue(listkey, r.list, { shouldDirty: true });
    return parsedSummary(r);
}
